import { IProduct } from "../../types/product.types";
import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";

const API_URL = import.meta.env.VITE_API_URL;

const makeRequestDebounced = createDebounce(makeRequest, 200);

export async function getProducts({
  status = [],
  search = "",
  page = 1,
  itemsPerPage = 10,
  orderBy = "name",
  fields = "name,code,category,brand,minQuantity,preventive,corrective,misuse, referencesSet, measuredunit, brand",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });
  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);

  const url = `${API_URL}/products?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IProduct[]
  > & { totalItems: number };

  return { ok: result.ok, data: result.data, totalItems: result.totalItems };
}

export async function getAllProducts() {
  const url = `${API_URL}/products-all`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<
    IProduct[]
  >;
  return { data: result.data };
}

export async function getSubPieces(productId: string | null) {
  const url = `${API_URL}/product-subpieces/${productId}`;
  return (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<{ product: IProduct; subpieces: IProduct[] }>
  >;
}

export async function getProductById(productId: string | null) {
  const url = `${API_URL}/product/${productId}`;
  return (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<IProduct>
  >;
}

export async function getProductByCode(productCode: string) {
  const url = `${API_URL}/product/code/${encodeURIComponent(productCode)}`;
  return (await makeRequestDebounced(url)) as Promise<
    ApiResponseType<IProduct>
  >;
}

export async function updateMeasuredUnit(data: {
  productId: string;
  measuredunit: string;
}) {
  const url = `${API_URL}/sub-product/measuredunit`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IProduct>
  >;
}

export async function createSubProduct(data: {
  productId: string;
  parentId?: string;
  name: string;
  maximumApplication: number;
  durability: number;
}) {
  const url = `${API_URL}/sub-product`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IProduct>
  >;
}

export type CreateProductPayload = Omit<IProduct, "_id" | "counterSet">;
export async function registerProduct(
  data: CreateProductPayload
): Promise<ApiResponseType<IProduct>> {
  const url = `${API_URL}/product-create`;
  const options: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IProduct>
  >;
}

export async function deleteProduct(productId: string) {
  const url = `${API_URL}/productdelete/${productId}`;
  const options: RequestInit = { method: "DELETE" };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IProduct>
  >;
}

export async function updateProduct(
  productId: string,
  data: Partial<IProduct>
) {
  const url = `${API_URL}/product-update/${productId}`;
  const options: RequestInit = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<
    ApiResponseType<IProduct>
  >;
}
